import React from 'react'
import { useIntl } from 'react-intl'
import { Link } from 'react-router-dom'
import {
    Box,
    IconButton,
    LinearProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableFooter,
    TableHead,
    TablePagination,
    TableRow,
} from '@mui/material'
import TablePaginationActions from '../../../../components/muiComponents/tablePaginationActions/TablePaginationActions'
import ArrowRightIcon from '../../../../assets/icons/Arrows-chevron/Arrow-Right.svg'
import { StudentSectionAnalysisState } from '../../../../features/types'
import { capitalize } from '../../../../utils/functions'

const styles = {
    tabelContainer: {
        border: 1,
        borderColor: 'grey.100',
        borderRadius: 2,
    },
    progressBox: {
        display: 'grid',
        gridTemplateColumns: 'auto auto',
        alignItems: 'center',
        gap: 2,
    },
    progress: {
        height: '6px',
        borderRadius: 5,
    },
}

interface Props {
    students: StudentSectionAnalysisState[]
    pageNumber: number
    totalCount: number
    isFetching: boolean
    page: number
    rowsPerPage: number
    setPage: (page: number) => void
    setRowsPerPage: (rowsPerPage: number) => void
}

const GroupStudentsTable: React.FC<Props> = ({
    students = [],
    isFetching = false,
    pageNumber = 0,
    totalCount = 0,
    page = 0,
    rowsPerPage = 0,
    setPage,
    setRowsPerPage,
}) => {
    const { formatMessage } = useIntl()

    // Avoid a layout jump when reaching the last page with empty rows.
    // const emptyRows =
    //     page > 0
    //         ? Math.max(0, (1 + page) * rowsPerPage - studentsData.length)
    //         : 0

    const emptyRows = page > 1 ? Math.max(0, rowsPerPage - students.length) : 0

    const handleChangePage = (
        event: React.MouseEvent<HTMLButtonElement> | null,
        newPage: number,
    ) => {
        setPage(newPage + 1)
    }

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(1)
    }

    return (
        <Box>
            <TableContainer
                component={Paper}
                sx={styles.tabelContainer}
                elevation={0}
            >
                <Table
                    sx={{ minWidth: 500 }}
                    aria-label="custom pagination table"
                >
                    <TableHead sx={{ bgcolor: 'grey.100' }}>
                        <TableRow>
                            <TableCell>#</TableCell>

                            <TableCell>
                                {formatMessage({ id: 'fullName' })}
                            </TableCell>

                            <TableCell>
                                {formatMessage({ id: 'group' })}
                            </TableCell>

                            <TableCell>
                                {capitalize(
                                    formatMessage({ id: 'videoLesson' }),
                                )}
                            </TableCell>

                            <TableCell>
                                {formatMessage({ id: 'test' })}
                            </TableCell>

                            <TableCell align="right">
                                {formatMessage({ id: 'details' })}
                            </TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {students.length
                            ? students.map((row, index) => (
                                  <TableRow key={row.userId}>
                                      <TableCell
                                          component="th"
                                          scope="row"
                                      >
                                          {index + 1}
                                      </TableCell>

                                      <TableCell
                                          component="th"
                                          scope="row"
                                      >
                                          {row.firstName + ' ' + row.lastName}
                                      </TableCell>

                                      <TableCell
                                          component="th"
                                          scope="row"
                                      >
                                          {row.title}
                                      </TableCell>

                                      <TableCell
                                          component="th"
                                          scope="row"
                                      >
                                          <Box sx={styles.progressBox}>
                                              <LinearProgress
                                                  value={Math.round(
                                                      row.watchedVideoPercentage,
                                                  )}
                                                  variant="determinate"
                                                  style={styles.progress}
                                              />

                                              <Box>
                                                  {Math.round(
                                                      row.watchedVideoPercentage,
                                                  )}
                                                  %
                                              </Box>
                                          </Box>
                                      </TableCell>

                                      <TableCell
                                          component="th"
                                          scope="row"
                                      >
                                          <Box sx={styles.progressBox}>
                                              <LinearProgress
                                                  value={Math.round(
                                                      row.topicTestPercentage,
                                                  )}
                                                  variant="determinate"
                                                  style={styles.progress}
                                              />

                                              <Box>
                                                  {Math.round(
                                                      row.topicTestPercentage,
                                                  )}
                                                  %
                                              </Box>
                                          </Box>
                                      </TableCell>

                                      <TableCell
                                          style={{ width: 60 }}
                                          align="right"
                                      >
                                          <Link
                                              to={`${row?.userId}/${row?.userCourseEnrollId}?courseSubjectId=${row?.courseSubjectId}`}
                                              state={{
                                                  student: row,
                                              }}
                                          >
                                              <IconButton
                                                  sx={{ background: '#f3f4f5' }}
                                              >
                                                  <img
                                                      src={ArrowRightIcon}
                                                      alt="edit"
                                                  />
                                              </IconButton>
                                          </Link>
                                      </TableCell>
                                  </TableRow>
                              ))
                            : !isFetching && (
                                  <TableRow>
                                      <TableCell
                                          component="th"
                                          scope="row"
                                          align="center"
                                          colSpan={10}
                                      >
                                          {formatMessage({ id: 'noData' })}
                                      </TableCell>
                                  </TableRow>
                              )}

                        {emptyRows > 0 && (
                            <TableRow style={{ height: 53 * emptyRows }}>
                                <TableCell colSpan={6} />
                            </TableRow>
                        )}
                    </TableBody>

                    <TableFooter>
                        <TableRow>
                            <TablePagination
                                rowsPerPageOptions={[
                                    5,
                                    10,
                                    25,
                                    { label: 'All', value: -1 },
                                ]}
                                colSpan={6}
                                count={totalCount}
                                rowsPerPage={rowsPerPage}
                                page={pageNumber}
                                SelectProps={{
                                    inputProps: {
                                        'aria-label': 'rows per page',
                                    },
                                    native: true,
                                }}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                ActionsComponent={TablePaginationActions}
                                sx={{ border: 0 }}
                            />
                        </TableRow>
                    </TableFooter>
                </Table>
            </TableContainer>
        </Box>
    )
}

export default GroupStudentsTable
